import { Card } from 'components/Card';
import React, { FC, KeyboardEventHandler, useCallback } from 'react';
import cx from 'clsx';
import styles from './styles.module.scss';
import { HistoricOrder } from 'olo-service';
import { useTranslation } from 'react-i18next';
import { InlineCommaList } from 'components/InlineCommaList/InlineCommaList';
import { PREVIOUS_ORDER_CARD } from '../const';
import { useOrderHistory } from 'hooks/context/OrderHistory';
import { useCartPreview } from 'hooks/context/CartPreview';
import { useCart } from 'hooks/context/cart';
import { useWindowSize } from 'hooks/breakpoints';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { KEY } from 'util/Input';
import { useMerchantState } from 'hooks/context/MerchantState';
import { usePrompt } from 'hooks/context/Prompt';

type PreviousOrderCardProps = React.HTMLAttributes<HTMLElement> & {
  order: HistoricOrder;
}

export const PreviousOrderCard : FC<PreviousOrderCardProps> = ({ className, order, onClick: closePreviewCardModal, ...props }) => {
  const { t } = useTranslation();
  const { isDesktop } = useWindowSize();
  const navigate = useSmartNavigate();
  const { isAcceptingOrders } = useMerchantState();
  const { openCartPreview } = useCartPreview();
  const { items, clear } = useCart();
  const { getConciseLineItems, addHistoricItemsToCart, clearUnavailableItems } = useOrderHistory();
  const { hydratePromptModal, closePrompt } = usePrompt();
  const conciseItems = getConciseLineItems(order);

  const handlePreviousOrder = useCallback(()=>{
    addHistoricItemsToCart(order);
    isDesktop ? openCartPreview() : navigate('~cart');
  },[order, isDesktop, addHistoricItemsToCart, navigate, openCartPreview]);

  const startNewOrder = useCallback(() =>{
    closePrompt();
    clearUnavailableItems();
    clear();
    handlePreviousOrder();
  }, [clearUnavailableItems, clear, closePrompt, handlePreviousOrder]);

  const handleOnClick = useCallback(e => {
    if (items?.length > 0){
      hydratePromptModal({
        primaryButtonContent: t('startNewOrder.confirm'),
        secondaryButtonContent: t('startNewOrder.cancel'),
        iconName: 'newOrder',
        title: t('startNewOrder.title'),
        subtitle: t('startNewOrder.subtitle'),
        primaryOnClick: startNewOrder,
      });
    } else {
      handlePreviousOrder();
    }
    closePreviewCardModal?.(e);
  }, [items?.length, closePreviewCardModal, hydratePromptModal, t, startNewOrder, handlePreviousOrder]);

  const handleKeyDown: KeyboardEventHandler = useCallback(e => {
    if (e.key === KEY.ENTER) handleOnClick(e);
  }, [handleOnClick]);

  return (
    <Card {...props}
      tabIndex={0}
      className={cx(styles.previousOrder, className, { [styles.disableHover]: !isAcceptingOrders })}
      data-testid={PREVIOUS_ORDER_CARD.root}
      onClick={isAcceptingOrders ? handleOnClick : undefined}
      onKeyDown={isAcceptingOrders ? handleKeyDown : undefined}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h3 data-testid={PREVIOUS_ORDER_CARD.heading}>{t('previousOrder.date', { date: new Date(order.timePlaced) })}</h3>
          {isDesktop && <div className={styles.orderAgain}>{t('previousOrder.orderAgain')}</div>}
        </div>
        <InlineCommaList className={cx(styles.list)} data-testid={PREVIOUS_ORDER_CARD.items}>
          {Object.entries(conciseItems).map(([key, item]) => (
            <li key={key}>
              {t('previousOrder.orderItem', { name: item.name, count: item.quantity })}
            </li>
          ))}
        </InlineCommaList>
      </div>
    </Card>
  );
};
